/**
 * KisiFirmalarSekmesi.jsx — denetmen kişi sayfasının (PersonelDetayPage) "Firmalar" sekmesinin gövdesi.
 * Backlog #365 (firmalı/firmasız anahtarı kalktı) + #370 (İşveren firma başına, backend #369) + #364 (donemler[]) + #383.
 *
 * İki sütun, ikisi de GET /personnel/{id}/firmalar'dan (bu bileşen kendisi okur; `reloadToken` değişince ve kendi
 * yazmalarından sonra yeniden):
 *   · ÇALIŞAN (sol)  — personel listesi / görevlendirme ilişkisi olan her firma (iliskiDurumu AKTIF, KAYITSIZ,
 *     SONLANDIRILDI): ilişki rozeti, rol rozetleri, "N fuar · M görev", Sonlandır / Geri Al (Daimî Liste'yle aynı
 *     pencereler; düğme görünürlüğü sunucudan hazır gelir, kural burada kurulmaz), açılır "Geçmiş" (donemler[] —
 *     eklenme, çalışan tipi, sonlandırma, geri alma — en yeni önce) ve üst çubuktaki fuarın durumu: kart-karari girdisi
 *     (`detay.firmalar[]`) varsa Eşleme/Görevlendirme satırı, yoksa "bu fuarda görevli değil" + firmanın fuara eşli olup
 *     olmadığı (BagDurumu → "Fuara eşle", Personel Ekle'deki akışın aynısı). "Firma Ekle" = PersonelEkleModal
 *     (kişi bilinen → T.C. sorgusu atlanır; hedef fuar üst çubuktaki).
 *   · İŞVEREN (sağ)  — `isveren === true` olan her firma (görevlendirmesi olmayan "YOK" satırları dahil): son değişiklik
 *     satırı, "İşveren değil yap" (onaylı) ve altta "İşveren Firma Ekle" (herhangi bir firma, işe alma şartı yok).
 *     Bir firma iki sütunda da olabilir (Hasan hem Kaya Dekor'un sahibi hem Atlas'ın çalışanı).
 *
 * Fuar bağımlı parçalar (Firma Ekle, eşleme durumu, fuara eşleme) fuar yokken kapalı/gizli; üstte tek satır uyarı.
 * Sigortalı firma kavramı yok (#383): `sigortaFirma*` hiçbir yerde okunmaz.
 *
 * ⚠️ Firma Ekle penceresi sonuç ekranında AÇIK kalır; sayfa kişiyi yeniden okuyunca (tam sayfa yükleniyor) bu bileşen ve
 * pencere sökülürdü. Bu yüzden turlar boyunca yalnızca kendi verimiz tazelenir, sayfa (`onDegisti`) pencere kapanınca çağrılır.
 *
 * @param {object}   person       Kişi Listesi satırı (`detay.kisi`): personId, name, surname, forbidden, tcNo, ...
 * @param {number}   personId
 * @param {object}   detay        GET /detay yanıtı; `firmalar[]` = üst çubuk fuarındaki kart-karari girdileri
 *                                (`.kisi` = o firmanın okuması, eslesme* + eslesmeGecmisi; `isverenFirmaId/Adi`)
 * @param {number}   fuarId       üst çubuktaki fuar; yoksa null
 * @param {string}   fuarAdi      o fuarın adı
 * @param {number}   reloadToken  değişince /firmalar yeniden okunur
 * @param {boolean}  readOnly     birleştirilmiş kart: tüm yazma denetimleri gizli
 * @param {Function} onDegisti    (mesaj) — bir yazma bitti: sayfa kişiyi yeniden okur, mesajı gösterir
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner,
} from "reactstrap";
import { Briefcase, Building2, ChevronDown, ChevronUp, Plus, Undo2, UserMinus } from "lucide-react";
import PersonelEkleModal, { BagDurumu, kisiSatirindanBilinenKisi } from "../PersonelEkleModal";
import FirmaFuarEsleModal from "../FirmaFuarEsleModal";
import { IliskiSonlandirModal, IliskiGeriAlModal } from "../PersonelRosterEylemleri";
import { EslesmeDurumRozeti, EslesmeSatiri } from "../PersonelDurumParcalari";
import { firmaBelgeleriUrl } from "../kisiBaglantilari";
import FirmaSearchSelect from "../../Firma/FirmaSearchSelect";
import { STATUS_HEX } from "../../Projects/StandPills";
import { getKisiFirmalari } from "../../../services/personnelAdminService";
import { removeFromRoster, undoRosterEnding } from "../../../services/personelRosterService";
import { getFirmaFuarBaglari } from "../../../services/kisiFirmaEsleService";
import { setKisiFirmaIsveren } from "../../../services/kisiFirmaIsverenService";
import { ISVEREN_ETIKETI } from "../../../services/kartDurumuService";
import { currentLocaleTag } from "../../../i18n";

const SECIM_YOK = "Önce üst çubuktan bir fuar seçin";
/** Çalışan sütununda bu kadar satırdan sonrası "Tümünü göster"in arkasında. */
const ILK_SATIR = 5;

const ILISKI_ROZET = {
  // Bu temada `success` gri, `secondary` yeşil basılır: olumlu durum `secondary`.
  AKTIF: { color: "secondary", baslik: "Firmanın güncel personel listesinde" },
  KAYITSIZ: { color: "light", baslik: "Görevlendirmesi var, firmanın personel listesinde kaydı yok" },
  SONLANDIRILDI: { color: "danger", baslik: "Firma ile ilişki sonlandırıldı" },
};

const firmaAdiYaz = (f) => f.firmaShortname || f.firmaName || `Firma #${f.firmaId}${f.firmaName === null ? " (silinmiş)" : ""}`;

/** "2026-09-18 14:00:00" / ISO → "18.09.2026 14:00"; yalnız tarih gelirse "18.09.2026"; çözülemezse olduğu gibi. */
function tarihYaz(v) {
  if (!v) return null;
  const s = String(v).replace(" ", "T");
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(v);
  return s.length > 10
    ? d.toLocaleString(currentLocaleTag(), { dateStyle: "short", timeStyle: "short" })
    : d.toLocaleDateString(currentLocaleTag(), { timeZone: "UTC" });
}

const birlestir = (...parcalar) => parcalar.filter(Boolean).join(" · ");

/** `/firmalar` satırı → Daimî Liste pencerelerinin okuduğu satır şekli (`rosterId`, `aktif`, bitiş alanları). */
function rosterSatiri(f, person, personId) {
  return {
    rosterId: f.rosterId, personId: person?.personId ?? personId, ad: person?.name, soyad: person?.surname, firmaAdi: firmaAdiYaz(f),
    aktif: f.iliskiDurumu !== "SONLANDIRILDI",
    bitisTarihi: f.bitisTarihi, bitirenAdi: f.bitirenAdi, bitisSebebi: f.bitisSebebi,
    bitisKaynagi: f.bitisKaynagi, bitisKaynagiAdi: f.bitisKaynagiAdi,
  };
}

// ─── Geçmiş (donemler[]) ─────────────────────────────────────────────────────────────

const OLAY_RENGI = { ekle: STATUS_HEX.success, tip: STATUS_HEX.info, bitis: STATUS_HEX.danger, geri: STATUS_HEX.warning };

function zamanDegeri(t) {
  const ms = t ? new Date(String(t).replace(" ", "T")).getTime() : NaN;
  return Number.isNaN(ms) ? -Infinity : ms;
}

/** Bir firmanın personel listesi dönemleri → tek düz olay listesi, en yeni önce (tarihsiz olaylar sona, sıra korunur). */
function olaylariKur(donemler) {
  const olaylar = [];
  (donemler || []).forEach((d) => {
    olaylar.push({ tur: "ekle", tarih: d.eklenmeTarihi, d });
    (d.calisanTipiGecmisi || []).forEach((g) => olaylar.push({ tur: "tip", tarih: g.tarih, g }));
    if (d.bitisTarihi) olaylar.push({ tur: "bitis", tarih: d.bitisTarihi, b: d, geriAlindi: false });
    (d.geriAlinanBitisler || []).forEach((b) => {
      olaylar.push({ tur: "bitis", tarih: b.bitisTarihi, b, geriAlindi: true });
      olaylar.push({ tur: "geri", tarih: b.geriAlmaTarihi, b });
    });
  });
  return olaylar
    .map((o, i) => ({ ...o, sira: i, zaman: zamanDegeri(o.tarih) }))
    .sort((a, b) => (b.zaman === a.zaman ? a.sira - b.sira : b.zaman - a.zaman));
}

function Sebep({ metin }) {
  return metin ? <span className="fst-italic text-muted"> — “{metin}”</span> : null;
}

function OlayMetni({ o }) {
  if (o.tur === "ekle") {
    const d = o.d;
    const ekleyen = d.ekleyenAdi || d.ekleyenUserId != null
      ? birlestir(d.ekleyenAdi, d.ekleyenSaka ? "SAKA" : "Firma")
      : null;
    return (
      <>
        <strong>Eklendi</strong>{" — "}{birlestir(tarihYaz(d.eklenmeTarihi), ekleyen, d.calisanTipiAdi && `Çalışan tipi: ${d.calisanTipiAdi}`)}
      </>
    );
  }
  if (o.tur === "tip") {
    const g = o.g;
    return (
      <>
        <strong>Çalışan tipi</strong>{": "}{g.oncekiTipAdi ?? "—"}{" → "}{g.yeniTipAdi ?? "—"}
        {" — "}{birlestir(tarihYaz(g.tarih), g.degistirenAdi, g.kaynakAdi)}
        <Sebep metin={g.aciklama} />
      </>
    );
  }
  if (o.tur === "bitis") {
    const b = o.b;
    return (
      <>
        <strong>Sonlandırıldı</strong>{o.geriAlindi && <span className="text-muted">{" (geri alındı)"}</span>}
        {" — "}{birlestir(tarihYaz(b.bitisTarihi), b.bitirenAdi, b.bitisKaynagiAdi)}
        <Sebep metin={b.bitisSebebi} />
      </>
    );
  }
  const b = o.b;
  return (
    <>
      <strong>Geri alındı</strong>{" — "}{birlestir(tarihYaz(b.geriAlmaTarihi), b.geriAlanAdi)}
      <Sebep metin={b.geriAlmaSebebi} />
    </>
  );
}

/** Firmanın personel listesi geçmişi (+ üst çubuk fuarındaki eşleme cevap geçmişi). */
function FirmaGecmisi({ donemler, eslemeGecmisi, fuarAdi }) {
  const olaylar = olaylariKur(donemler);
  return (
    <div className="mt-2">
      {olaylar.length > 0 && (
        <div className="ms-2 ps-3 border-start">
          {olaylar.map((o) => (
            <div key={o.sira} className="position-relative pb-2 small">
              <span
                className="position-absolute rounded-circle"
                style={{ left: -20, top: 5, width: 8, height: 8, backgroundColor: OLAY_RENGI[o.tur] }}
              />
              <OlayMetni o={o} />
            </div>
          ))}
        </div>
      )}
      {eslemeGecmisi.length > 0 && (
        <div className={olaylar.length > 0 ? "mt-2" : ""}>
          <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: "0.7rem" }}>
            Eşleme geçmişi{fuarAdi ? ` · ${fuarAdi}` : ""}
          </div>
          <div className="vstack gap-1">
            {eslemeGecmisi.map((g, sira) => ({ ...g, sira })).map((g) => (
              <div key={g.sira} className="small">
                <EslesmeDurumRozeti durum={g.durum}>{g.durumAdi}</EslesmeDurumRozeti>
                <span className="text-muted ms-2">
                  {birlestir(g.kararVerenAdi, tarihYaz(g.kararTarihi))}{g.islem === 2 ? " · görevlendirerek kabul etti" : ""}
                </span>
                {g.redSebebi && <div className="text-danger fst-italic">“{g.redSebebi}”</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Çalışan satırı ──────────────────────────────────────────────────────────────────

function CalisanSatiri({
  f, ilk, fuarId, fuarAdi, girdi, bag, readOnly, acik, onToggle, onSonlandir, onGeriAl, onEsle,
}) {
  const rozet = ILISKI_ROZET[f.iliskiDurumu] ?? ILISKI_ROZET.KAYITSIZ;
  const guncel = f.iliskiDurumu === "AKTIF" || f.iliskiDurumu === "KAYITSIZ";
  const gorev = fuarId
    ? (f.gorevler || []).find((g) => Number(g.fuarId) === Number(fuarId)) ?? null
    : null;
  const fuardaGorevli = Boolean(girdi) || Boolean(gorev);
  const meslek = (gorev?.meslekler || []).map((m) => m.meslekAdi).filter(Boolean).join(", ") || gorev?.meslekAdi || null;
  const kisi = girdi?.kisi ?? null;
  const eslemeGecmisi = Array.isArray(kisi?.eslesmeGecmisi) ? kisi.eslesmeGecmisi : [];
  const donemler = f.donemler || [];
  const gecmisVar = donemler.length > 0 || eslemeGecmisi.length > 0;

  return (
    <div className={`py-2 ${ilk ? "" : "border-top"}`}>
      <div className="d-flex align-items-start gap-2">
        <div className="flex-grow-1 min-w-0">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="fw-semibold text-break" title={f.firmaName || undefined}>{firmaAdiYaz(f)}</span>
            <Badge
              color={rozet.color} className={`fw-normal ${rozet.color === "light" ? "text-body border" : ""}`}
              title={rozet.baslik}
            >
              {f.iliskiDurumuAdi || f.iliskiDurumu}
            </Badge>
            <Badge color="light" className="text-body border fw-normal">Çalışan</Badge>
            {f.isveren && <Badge color="primary" className="fw-normal">İşveren</Badge>}
            {f.gorevSayisi > 0 && (
              <span className="text-muted" style={{ fontSize: "0.75rem" }}>{f.fuarSayisi} fuar · {f.gorevSayisi} görev</span>
            )}
          </div>

          {/* Üst çubuktaki fuarın durumu */}
          {fuarId && fuardaGorevli && (
            <div className="mt-1">
              {kisi?.eslesmeDurumu != null ? (
                <EslesmeSatiri record={kisi} />
              ) : gorev?.eslesmeDurumu != null ? (
                <div className="small">
                  <EslesmeDurumRozeti durum={gorev.eslesmeDurumu}>Görevlendirme: {gorev.eslesmeDurumuAdi}</EslesmeDurumRozeti>
                </div>
              ) : (
                <div className="small text-muted">Bu fuarda görevli</div>
              )}
              {meslek && <div className="small text-muted mt-1">Meslek: {meslek}</div>}
            </div>
          )}
          {fuarId && !fuardaGorevli && guncel && (
            <div className="mt-1">
              <div className="small text-muted">Bu fuarda görevli değil</div>
              {/* `firmaArayan`: Fuara eşle bağlantısını gizler — salt okunur kartta yazma yok. */}
              <BagDurumu bag={bag} firmaArayan={readOnly} onEsle={onEsle} />
            </div>
          )}
          {girdi?.isverenFirmaId && (
            <div className="small mt-1">
              <Link to={firmaBelgeleriUrl({ firmaId: girdi.isverenFirmaId, fuarId })}>
                {ISVEREN_ETIKETI} ({girdi.isverenFirmaAdi || `Firma #${girdi.isverenFirmaId}`})
              </Link>
            </div>
          )}

          {gecmisVar && (
            <Button color="link" size="sm" className="p-0 mt-1 text-decoration-none" style={{ fontSize: "0.78rem" }} onClick={onToggle}>
              {acik ? <ChevronUp size={13} className="me-1" /> : <ChevronDown size={13} className="me-1" />}
              Geçmiş{donemler.length > 0 ? ` (${donemler.length})` : ""}
            </Button>
          )}
        </div>

        {!readOnly && (
          <div className="d-flex gap-1 flex-shrink-0">
            {f.sonlandirilabilir && (
              <Button size="sm" color="danger" outline title="Bu firma ile ilişkiyi sonlandır" onClick={() => onSonlandir(f)}>
                <UserMinus size={13} className="me-1" />Sonlandır
              </Button>
            )}
            {f.geriAlinabilir && (
              <Button size="sm" color="warning" outline title="Sonlandırılan ilişkiyi geri al" onClick={() => onGeriAl(f)}>
                <Undo2 size={13} className="me-1" />Geri Al
              </Button>
            )}
          </div>
        )}
      </div>
      {acik && gecmisVar && <FirmaGecmisi donemler={donemler} eslemeGecmisi={eslemeGecmisi} fuarAdi={fuarId ? fuarAdi : null} />}
    </div>
  );
}

// ─── İşveren satırı ──────────────────────────────────────────────────────────────────

/** Son değişiklik satırı; izi olmayan (kimsenin dokunmadığı) eski işaret "Eski kayıt". */
function isverenSonDegisiklik(f) {
  const d = f.isverenDegisikligi;
  if (!d) return f.isverenKaynakAdi || "Eski kayıt";
  const kim = d.degistirenAdi || (d.degistirenUserId != null ? `#${d.degistirenUserId}` : null);
  return `Son değişiklik: ${birlestir(kim, tarihYaz(d.tarih)) || "—"}`;
}

function IsverenSatiri({ f, ilk, readOnly, busy, onKaldir }) {
  return (
    <div className={`d-flex align-items-start gap-2 py-2 ${ilk ? "" : "border-top"}`}>
      <div className="flex-grow-1 min-w-0">
        <div className="fw-semibold text-break" title={f.firmaName || undefined}>{firmaAdiYaz(f)}</div>
        <div className="text-muted" style={{ fontSize: "0.75rem" }}>{isverenSonDegisiklik(f)}</div>
      </div>
      {!readOnly && (
        <Button size="sm" color="danger" outline className="flex-shrink-0" disabled={busy} onClick={() => onKaldir(f)}>
          İşveren değil yap
        </Button>
      )}
    </div>
  );
}

// ─── Sekme ───────────────────────────────────────────────────────────────────────────

export default function KisiFirmalarSekmesi({
  person, personId, detay, fuarId = null, fuarAdi = null, reloadToken = 0, readOnly = false, onDegisti,
}) {
  const fuar = fuarId ? Number(fuarId) : null;
  const [veri, setVeri] = useState(null); // GET /personnel/{id}/firmalar yanıtı
  const [yukleniyor, setYukleniyor] = useState(true);
  const [okumaHatasi, setOkumaHatasi] = useState(null);
  const [yenile, setYenile] = useState(0); // kendi yazmalarımızdan sonra tazeleme
  const [hata, setHata] = useState(null);
  const [busy, setBusy] = useState(false);

  const [tumu, setTumu] = useState(false);
  const [acikGecmis, setAcikGecmis] = useState({}); // firmaId → true
  const [ekleAcik, setEkleAcik] = useState(false);
  const ekleDegisti = useRef(false);
  const [sonlandirHedef, setSonlandirHedef] = useState(null); // `/firmalar` satırı
  const [geriAlHedef, setGeriAlHedef] = useState(null);

  const [isverenSecim, setIsverenSecim] = useState(null); // FirmaSearchSelect değeri
  const [isverenKaldirHedef, setIsverenKaldirHedef] = useState(null); // `/firmalar` satırı

  // Fuar bağı kontrolü: `${firmaId}|${fuarId}|${bagToken}` → { data, error }. Efekt anahtarı string'den okur.
  const [baglar, setBaglar] = useState({});
  const [bagToken, setBagToken] = useState(0);
  const istenenBaglar = useRef(new Set());
  const [esleHedef, setEsleHedef] = useState(null); // `/firmalar` satırı

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (personId == null) return undefined;
    let iptal = false;
    async function oku() {
      setYukleniyor(true);
      setOkumaHatasi(null);
      try {
        const d = await getKisiFirmalari(personId);
        if (!iptal) setVeri(d);
      } catch (e) {
        if (!iptal) setOkumaHatasi(e.message || "Firmalar okunamadı.");
      } finally {
        if (!iptal) setYukleniyor(false);
      }
    }
    oku();
    return () => { iptal = true; };
  }, [personId, reloadToken, yenile]);

  const firmalar = veri?.firmalar || [];
  // YOK = "Yalnızca İşveren kaydı": yalnızca İşveren sütununa girer.
  const calisanlar = firmalar.filter((f) => f.iliskiDurumu !== "YOK");
  const isverenler = firmalar.filter((f) => f.isveren === true);
  const gorunenCalisan = tumu || calisanlar.length <= ILK_SATIR ? calisanlar : calisanlar.slice(0, ILK_SATIR);
  const girdiOf = (firmaId) => (detay?.firmalar || []).find((g) => Number(g.firmaId) === Number(firmaId)) ?? null;
  const gorevVarMi = (f) => Boolean(girdiOf(f.firmaId))
    || (f.gorevler || []).some((g) => Number(g.fuarId) === fuar);

  // Fuara eşli mi? Yalnızca bu fuarda görevli OLMAYAN güncel firmalar için (görevlisi zaten eşli sayılır).
  const bagKeyleriStr = fuar
    ? gorunenCalisan
      .filter((f) => (f.iliskiDurumu === "AKTIF" || f.iliskiDurumu === "KAYITSIZ") && !gorevVarMi(f))
      .map((f) => `${f.firmaId}|${fuar}|${bagToken}`)
      .join(",")
    : "";
  useEffect(() => {
    if (!bagKeyleriStr) return;
    bagKeyleriStr.split(",").forEach((key) => {
      if (istenenBaglar.current.has(key)) return;
      istenenBaglar.current.add(key);
      const [firmaId, fuarKey] = key.split("|");
      getFirmaFuarBaglari(Number(firmaId), Number(fuarKey))
        .then((data) => setBaglar((p) => ({ ...p, [key]: { data, error: null } })))
        .catch((e) => setBaglar((p) => ({ ...p, [key]: { data: null, error: e.message } })));
    });
  }, [bagKeyleriStr]);
  const bagOf = (firmaId) => baglar[`${firmaId}|${fuar}|${bagToken}`] ?? null;

  // Kişi Listesi'ndeki "Fuara Eşle" ile aynı engeller.
  const eklemeEngeli = person?.forbidden
    ? "Girişi yasaklı — fuara eşlenemez"
    : !fuar ? "Eşlemek için üst çubuktan bir fuar seçin" : null;

  const adSoyad = `${person?.name ?? ""} ${person?.surname ?? ""}`.trim() || "Bu kişi";
  const isverenFirmaIdleri = new Set(isverenler.map((f) => Number(f.firmaId)));
  const secilenZatenIsveren = isverenSecim != null && isverenFirmaIdleri.has(Number(isverenSecim.firmaId));

  async function sonlandir(sebep) {
    if (!sonlandirHedef) return;
    setBusy(true);
    setHata(null);
    try {
      const mesaj = await removeFromRoster(sonlandirHedef.firmaId, personId, { sebep });
      setSonlandirHedef(null);
      setYenile((n) => n + 1);
      onDegisti?.(mesaj || "İlişki sonlandırıldı.");
    } catch (e) {
      setSonlandirHedef(null);
      setHata(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function geriAl(sebep) {
    if (!geriAlHedef) return;
    setBusy(true);
    setHata(null);
    try {
      const mesaj = await undoRosterEnding(geriAlHedef.firmaId, personId, { sebep });
      setGeriAlHedef(null);
      setYenile((n) => n + 1);
      onDegisti?.(mesaj || "İlişki geri alındı.");
    } catch (e) {
      // 409 = arada yeniden eklenmiş: pencere kapanır, liste tazelenir (Daimî Liste ile aynı).
      setGeriAlHedef(null);
      if (e.status === 409) setYenile((n) => n + 1);
      setHata(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function isverenYaz(firmaId, firmaAdi, deger) {
    setBusy(true);
    setHata(null);
    try {
      const sonuc = await setKisiFirmaIsveren(personId, firmaId, deger);
      setIsverenKaldirHedef(null);
      setIsverenSecim(null);
      setYenile((n) => n + 1);
      if (sonuc?.degisti === false) {
        onDegisti?.(deger
          ? `${firmaAdi} için zaten İşveren olarak işaretliydi.`
          : `${firmaAdi} için İşveren işareti zaten yoktu.`);
      } else {
        onDegisti?.(deger
          ? `${adSoyad}, ${firmaAdi} için İşveren olarak işaretlendi.`
          : `${adSoyad} için ${firmaAdi} firmasındaki İşveren işareti kaldırıldı.`);
      }
    } catch (e) {
      setIsverenKaldirHedef(null);
      setHata(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {!fuar && <div className="text-muted small mb-2">{SECIM_YOK}</div>}
      {hata && <Alert fade={false} color="danger" className="py-2 small" toggle={() => setHata(null)}>{hata}</Alert>}
      {okumaHatasi && !veri && <Alert fade={false} color="danger" className="py-2 small">{okumaHatasi}</Alert>}

      <Row className="g-3">
        {/* ── Çalışan ─────────────────────────────────────────────────────────── */}
        <Col lg={7}>
          <Card className="h-100 mb-0">
            <CardHeader className="bg-transparent d-flex align-items-center gap-2 flex-wrap">
              <Building2 size={16} className="text-muted" />
              <span className="fw-semibold">Çalışan</span>
              {calisanlar.length > 0 && <Badge color="light" className="text-body border fw-normal">{calisanlar.length}</Badge>}
              {!readOnly && (
                <span className="ms-auto" title={eklemeEngeli || `Kişiyi bir firmaya ekle ve ${fuarAdi || "üst çubuktaki fuara"} eşle`}>
                  <Button color="green" size="sm" disabled={Boolean(eklemeEngeli)} onClick={() => setEkleAcik(true)}>
                    <Plus size={13} className="me-1" />Firma Ekle
                  </Button>
                </span>
              )}
            </CardHeader>
            <CardBody className="py-2">
              {yukleniyor && !veri && <div className="py-2"><Spinner size="sm" color="primary" /></div>}
              {veri && calisanlar.length === 0 && <div className="text-muted small py-2">Çalışan olduğu firma yok.</div>}

              {gorunenCalisan.map((f, i) => (
                <CalisanSatiri
                  key={f.firmaId} f={f} ilk={i === 0} fuarId={fuar} fuarAdi={fuarAdi} girdi={girdiOf(f.firmaId)}
                  bag={bagOf(f.firmaId)} readOnly={readOnly}
                  acik={Boolean(acikGecmis[f.firmaId])}
                  onToggle={() => setAcikGecmis((p) => ({ ...p, [f.firmaId]: !p[f.firmaId] }))}
                  onSonlandir={setSonlandirHedef} onGeriAl={setGeriAlHedef} onEsle={() => setEsleHedef(f)}
                />
              ))}
              {gorunenCalisan.length < calisanlar.length && (
                <Button color="link" size="sm" className="px-0" onClick={() => setTumu(true)}>
                  Tümünü göster (+{calisanlar.length - gorunenCalisan.length})
                </Button>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* ── İşveren ─────────────────────────────────────────────────────────── */}
        <Col lg={5}>
          <Card className="h-100 mb-0">
            <CardHeader className="bg-transparent d-flex align-items-center gap-2">
              <Briefcase size={16} className="text-muted" />
              <span className="fw-semibold">İşveren</span>
              {isverenler.length > 0 && <Badge color="light" className="text-body border fw-normal">{isverenler.length}</Badge>}
            </CardHeader>
            <CardBody className="py-2">
              {yukleniyor && !veri && <div className="py-2"><Spinner size="sm" color="primary" /></div>}
              {veri && isverenler.length === 0 && <div className="text-muted small py-2">İşveren olduğu firma yok.</div>}

              {isverenler.map((f, i) => (
                <IsverenSatiri key={f.firmaId} f={f} ilk={i === 0} readOnly={readOnly} busy={busy} onKaldir={setIsverenKaldirHedef} />
              ))}

              {!readOnly && veri && (
                <div className={`pt-2 ${isverenler.length > 0 ? "mt-2 border-top" : ""}`}>
                  <div className="small fw-semibold mb-1">İşveren Firma Ekle</div>
                  <div className="d-flex align-items-start gap-2">
                    <div className="flex-grow-1 min-w-0">
                      <FirmaSearchSelect value={isverenSecim} onChange={setIsverenSecim} forceLive disabled={busy} />
                    </div>
                    <Button
                      color="green" size="sm" className="flex-shrink-0"
                      disabled={!isverenSecim || secilenZatenIsveren || busy}
                      onClick={() => isverenYaz(isverenSecim.firmaId, isverenSecim.firmaShortname || isverenSecim.firmaName || `Firma #${isverenSecim.firmaId}`, true)}
                    >
                      {busy ? <Spinner size="sm" /> : "Ekle"}
                    </Button>
                  </div>
                  {secilenZatenIsveren && <div className="small text-muted mt-1">Bu firmada zaten İşveren.</div>}
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* "İşveren değil yap" onayı */}
      <Modal isOpen={Boolean(isverenKaldirHedef)} toggle={() => !busy && setIsverenKaldirHedef(null)}>
        <ModalHeader toggle={() => !busy && setIsverenKaldirHedef(null)}>İşveren işareti kaldırılsın mı?</ModalHeader>
        <ModalBody className="small">
          {isverenKaldirHedef && (
            <>
              <p className="mb-2">
                <strong>{adSoyad}</strong>, <strong>{firmaAdiYaz(isverenKaldirHedef)}</strong> için İşveren olmaktan çıkarılacak.
              </p>
              <p className="mb-0 text-muted">
                Kişi bu firma üzerinden işveren yolundan kart alamaz; başka firmalardaki işveren esnekliği de buna bağlıysa
                kalkar. Daha önce basılmış kartlar geri alınmaz.
              </p>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setIsverenKaldirHedef(null)} disabled={busy}>Vazgeç</Button>
          <Button
            color="danger" disabled={busy}
            onClick={() => isverenYaz(isverenKaldirHedef.firmaId, firmaAdiYaz(isverenKaldirHedef), false)}
          >
            {busy ? <Spinner size="sm" /> : "İşareti kaldır"}
          </Button>
        </ModalFooter>
      </Modal>

      <IliskiSonlandirModal
        row={sonlandirHedef ? rosterSatiri(sonlandirHedef, person, personId) : null}
        busy={busy} onClose={() => !busy && setSonlandirHedef(null)} onConfirm={sonlandir}
      />
      <IliskiGeriAlModal
        row={geriAlHedef ? rosterSatiri(geriAlHedef, person, personId) : null}
        busy={busy} onClose={() => !busy && setGeriAlHedef(null)} onConfirm={geriAl}
      />

      {ekleAcik && fuar && (
        <PersonelEkleModal
          fuarId={fuar}
          fuarLabel={fuarAdi}
          bilinenKisi={kisiSatirindanBilinenKisi(person)}
          onClose={() => {
            setEkleAcik(false);
            if (ekleDegisti.current) { ekleDegisti.current = false; onDegisti?.("Firma bilgileri güncellendi."); }
          }}
          onDone={() => { ekleDegisti.current = true; setYenile((n) => n + 1); }}
        />
      )}

      {esleHedef && fuar && (
        <FirmaFuarEsleModal
          firma={{ firmaId: esleHedef.firmaId, firmaName: esleHedef.firmaName || firmaAdiYaz(esleHedef) }}
          fuarId={fuar}
          fuarLabel={fuarAdi}
          onClose={() => setEsleHedef(null)}
          onDone={() => { setEsleHedef(null); setBagToken((n) => n + 1); }}
        />
      )}
    </>
  );
}
